import { useQuery } from "@tanstack/react-query";
import { Alert, Skeleton, Tag } from "antd";

import { Seo } from "@/components/seo/Seo";
import { routePaths } from "@/routes/routePaths";
import { getAdminStats } from "@/services/adminApi";
import type { AdminStats } from "@/types/admin";

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value || 0);
}

function formatBytes(value: number): string {
  if (!value) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let size = value;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

export function AdminDashboardPage() {
  const statsQuery = useQuery({
    queryKey: ["admin", "stats"],
    queryFn: getAdminStats,
    refetchInterval: 60000
  });

  const stats = statsQuery.data;
  const cards = stats
    ? [
        ["Total requests", formatNumber(stats.totals.totalRequests)],
        ["Transcripts", formatNumber(stats.totals.transcriptRequests)],
        ["AI notes", formatNumber(stats.totals.summaryRequests)],
        ["Failures", formatNumber(stats.totals.failedRequests)],
        ["Input volume", formatBytes(stats.totals.inputBytes)],
        ["Avg duration", `${formatNumber(stats.totals.averageDurationMs)} ms`]
      ]
    : [];

  return (
    <>
      <Seo
        path={routePaths.admin.root}
        title="Admin Dashboard | VideoTranscriptSaver"
        description="Admin dashboard for VideoTranscriptSaver."
        robots="noindex,nofollow"
      />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-semibold">Admin dashboard</h1>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
            Monitor transcript usage, AI notes, provider health, and recent audit activity.
          </p>
        </div>

        {statsQuery.error ? (
          <Alert type="error" showIcon message={statsQuery.error.message} className="rounded-none" />
        ) : null}

        {statsQuery.isLoading ? <Skeleton active paragraph={{ rows: 8 }} /> : null}

        {stats ? (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map(([label, value]) => (
                <div key={label} className="border border-neutral-200 p-4 dark:border-neutral-800">
                  <p className="text-sm text-neutral-500">{label}</p>
                  <p className="mt-2 text-2xl font-semibold">{value}</p>
                </div>
              ))}
            </section>

            <section>
              <h2 className="text-xl font-semibold">Last 14 days</h2>
              <div className="mt-3 grid gap-2">
                {stats.byDay.length ? (
                  stats.byDay.map((day) => (
                    <div
                      key={day.day}
                      className="grid grid-cols-[96px_1fr_auto] items-center gap-3 text-sm"
                    >
                      <span className="font-mono text-xs text-neutral-500">{day.day}</span>
                      <span className="h-2 bg-neutral-100 dark:bg-neutral-900">
                        <span
                          className="block h-2 bg-neutral-950 dark:bg-white"
                          style={{
                            width: `${Math.min(100, Math.max(4, day.totalRequests * 8))}%`
                          }}
                        />
                      </span>
                      <span>{formatNumber(day.totalRequests)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-neutral-500">No audit rows yet.</p>
                )}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold">Recent activity</h2>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-[720px] border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 text-neutral-500 dark:border-neutral-800">
                      <th className="py-2 pr-4 font-medium">Action</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Source</th>
                      <th className="py-2 pr-4 font-medium">Provider</th>
                      <th className="py-2 pr-4 font-medium">Duration</th>
                      <th className="py-2 pr-4 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.length ? (
                      stats.recent.map((row) => (
                        <tr
                          key={`${row.createdAt}-${row.action}-${row.status}`}
                          className="border-b border-neutral-100 last:border-b-0 dark:border-neutral-900"
                        >
                          <td className="py-3 pr-4 capitalize">{row.action || "unknown"}</td>
                          <td className="py-3 pr-4">
                            <Tag color={row.status === "success" ? "green" : "red"} className="m-0 rounded-none">
                              {row.status || "unknown"}
                            </Tag>
                          </td>
                          <td className="py-3 pr-4">{row.sourceType || "-"}</td>
                          <td className="py-3 pr-4">{row.provider || "-"}</td>
                          <td className="py-3 pr-4">{formatNumber(row.durationMs)} ms</td>
                          <td className="py-3 pr-4">
                            {row.createdAt ? new Date(row.createdAt).toLocaleString() : "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="py-3 text-neutral-500" colSpan={6}>
                          No audit rows yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </div>
    </>
  );
}
